import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import { collectNestedGroupIdsPostOrder } from '@/lib/groups/shape-group-tree'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import {
  boxVerticesForShapeGeometry,
  canvasVecToLocal,
  localVecToCanvas,
  normalizeRotationDegrees,
} from '@/lib/transforms/geometry'

const MIN_GROUP_DIMENSION = 1e-4

/** Canvas-axis AABB of the union of each shape's OBB corners (same frame as group transforms). */
export function unionCanvasAabbFromShapeObbs(shapes: ShapeData[]): ObjectBounds | null {
  if (shapes.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const shape of shapes) {
    const verts = boxVerticesForShapeGeometry({
      x: shape.x,
      y: shape.y,
      width: shape.width ?? 0,
      height: shape.height ?? 0,
      rotation: shape.rotation ?? 0,
    })

    for (const v of verts) {
      minX = Math.min(minX, v.x)
      minY = Math.min(minY, v.y)
      maxX = Math.max(maxX, v.x)
      maxY = Math.max(maxY, v.y)
    }
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null

  return {
    x: minX,
    y: minY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  }
}

/**
 * Tight kind-7 group row x/y/w/h in OBB layout space for `group.rotation`, from the union of each
 * member shape's OBB corners (matches `applyGroupTransformToDescendants`).
 */
export function computeGroupBoundsFromShapes(
  group: ShapeData,
  members: ShapeData[]
): ObjectBounds | null {
  if (members.length === 0) return null

  if (!Number.isFinite(group.x) || !Number.isFinite(group.y)) return null

  const gr = normalizeRotationDegrees(group.rotation ?? 0)
  const w0 = group.width ?? 0
  const h0 = group.height ?? 0
  const pivot = {
    x: group.x + w0 / 2,
    y: group.y + h0 / 2,
  }

  let minLx = Infinity
  let maxLx = -Infinity
  let minLy = Infinity
  let maxLy = -Infinity

  for (const shape of members) {
    const verts = boxVerticesForShapeGeometry({
      x: shape.x,
      y: shape.y,
      width: shape.width ?? 0,
      height: shape.height ?? 0,
      rotation: shape.rotation ?? 0,
    })

    for (const v of verts) {
      const local = canvasVecToLocal({ x: v.x - pivot.x, y: v.y - pivot.y }, gr)

      minLx = Math.min(minLx, local.x)
      maxLx = Math.max(maxLx, local.x)
      minLy = Math.min(minLy, local.y)
      maxLy = Math.max(maxLy, local.y)
    }
  }

  const width = Math.max(MIN_GROUP_DIMENSION, maxLx - minLx)
  const height = Math.max(MIN_GROUP_DIMENSION, maxLy - minLy)
  const midLocal = { x: (minLx + maxLx) / 2, y: (minLy + maxLy) / 2 }
  const midCanvas = localVecToCanvas(midLocal, gr)
  const center = { x: pivot.x + midCanvas.x, y: pivot.y + midCanvas.y }

  return {
    x: center.x - width / 2,
    y: center.y - height / 2,
    width,
    height,
  }
}

/** Represent a text box as a synthetic (unrotatable-fill) shape for OBB/bounds math. */
export function textBoxAsObbShape(box: TextBoxData): ShapeData {
  return {
    id: box.id,
    type: 'square',
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    rotation: box.rotation ?? 0,
  }
}

/** Same as `computeGroupBoundsFromShapes` for shapes whose `parentGroupId` is `groupId`. */
export function computeGroupBoundsFromDirectChildren(
  list: ShapeData[],
  groupId: string,
  group: ShapeData
): ObjectBounds | null {
  const directChildren = list.filter((shape) => shape.parentGroupId === groupId)

  if (directChildren.length === 0) return null

  return computeGroupBoundsFromShapes(group, directChildren)
}

/**
 * Live (never-stale) rect for a group's text-wrap boundary. Resolves nested child groups' own
 * live bounds first (post-order, via `collectNestedGroupIdsPostOrder`), so an outer group's union
 * is built from an inner group's freshly computed box rather than its possibly-stale stored
 * `x/y/width/height` — the group's stored fields are only refreshed by `recomputePassiveGroupBounds`
 * (`shape-manager/hooks/use-shape-grouping.ts`), which `center-canvas/hooks/use-selection-history.ts` calls on
 * entering/exiting group isolation, not on every child drag while a user
 * is actively editing inside an isolated group. This is the pure, non-mutating read-only sibling of
 * that function: same patch-in-post-order traversal, no `setShapes`/history/persistence. Returns
 * `null` for an empty group (e.g. mid-ungroup) so callers can fall back to the group's own stored
 * rect instead of throwing.
 */
export function computeLiveGroupWrapRect(
  shapes: ShapeData[],
  groupId: string
): (ObjectBounds & { rotation: number }) | null {
  const order = collectNestedGroupIdsPostOrder(shapes, groupId)
  let working = shapes
  let result: ObjectBounds | null = null

  for (const id of order) {
    const group = working.find((shape) => shape.id === id)

    if (!group || !isGroupShape(group)) continue
    const bounds = computeGroupBoundsFromDirectChildren(working, id, group)

    if (!bounds) continue
    working = working.map((shape) => (shape.id === id ? { ...shape, ...bounds } : shape))
    if (id === groupId) result = bounds
  }

  if (!result) return null

  const group = shapes.find((shape) => shape.id === groupId)

  return { ...result, rotation: group?.rotation ?? 0 }
}
